// 몹 셰이더 모션: 스키닝 없이 정점 셰이더(TSL)에서 상태별 자세를 만든다.
// 인스턴스 속성 3개로 CPU와 소통한다(프레임 루프에서 새 객체를 만들지 않고 배열만 덮어쓴다).
//   aM = (상태 MobState, 진행률 0..1, 위상, 피격 플래시 0..1)
//   aN = (yaw, 크기, 속도 0..1, 플래그: 엘리트 1 + 방패 변종 2)
//   aT = (발 위치 x, y, z, 0)
// 모델 공간(발이 원점, +Z 정면)에서 휘고(허리 피벗) 눌리고 뛴 뒤, yaw·크기·위치로 월드에 놓는다.
// 인스턴스 행렬은 쓰지 않는다(positionNode가 월드 위치를 직접 만든다). 그림자 패스도 같은 positionNode를 쓴다.
import * as THREE from 'three/webgpu';
import * as TSL from 'three/tsl';
import { MobState } from '../../shared/types';
import { KNOCKBACK } from '../../shared/config';
import { skinNodes, type SkinAtlas } from './skinning';

// TSL 노드 타입은 느슨하게 다룬다(식 조립용).
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type N = any;
// TSL 식 조립은 타입을 느슨하게 쓴다(오버로드 추론이 any 인자에서 vec3로 번진다).
const { abs, attribute, cameraPosition, cos, dot, float, fract, normalGeometry, normalize, positionGeometry, screenCoordinate, sin, smoothstep, step, transformNormalToView, vec2, vec3 } = TSL as unknown as Record<string, N>;

export type MobShape = 'goblin' | 'boar' | 'shaman';

export interface MobMotionNodes {
  /** 월드 위치(정점 단계) */
  position: N;
  /** 월드 법선 */
  normalWorld: N;
  /** 뷰 공간 법선(정점 단계 varying) */
  normalView: N;
  /** 사망 디더 페이드 0..1 */
  fade: N;
  hit: N;
  /** 엘리트 0/1, 방패 변종 0/1 */
  elite: N;
  variant: N;
  /** 스페셜 몬스터 표지 0/1: 황금 고블린(몸 금색), 챔피언(금빛 테두리) */
  gold: N;
  champ: N;
  /** 상태 위상(림 맥동용) */
  phase: N;
}

/** 화면 좌표 기반 디더 문턱(0..1). fade 이상이면 남긴다. */
export function ditherKeep(fade: N): N {
  const ign = fract(float(52.9829189).mul(fract(dot(screenCoordinate.xy, vec2(0.06711056, 0.00583715)))));
  return step(fade, ign);
}

/**
 * 사망 소멸(2026-11-04 RG-013): 화면 고정 디더 대신 몸에 붙은 잡음으로 타들어 가듯 사라진다.
 * 한꺼번에 여럿이 쓰러지면 화면 디더가 몹 무리 전체를 격자 무늬로 덮어 지저분했다.
 * rest = 모델 공간 정지 위치(m). keep 1이면 그린다, edge는 타는 가장자리 0..1(발광용).
 */
export function dissolveNodes(fade: N, rest: N): { keep: N; edge: N } {
  const n = TSL.mx_noise_float(rest.mul(3.1) as never) as N;
  const v = n.mul(0.5).add(0.5).clamp(0, 1);
  // fade 0이면 문턱 -0.08(전부 남김), 1이면 1.08(전부 지움)
  const thr = fade.mul(1.16).sub(0.08);
  const keep = step(thr, v);
  const edge = float(1).sub(smoothstep(0, 0.07, v.sub(thr))).mul(keep).mul(step(0.001, fade));
  return { keep, edge };
}

const easeOut = (t: N): N => t.mul(float(2).sub(t));
/** 조명 법선에서 원래 정점 법선의 비중(나머지는 이웃 평균 매끈 법선) */
const LIGHT_RAW = 0.25;

/**
 * 몹 모션 노드 묶음.
 * @param height 모델 높이(m, 스케일 전)
 * @param thick 외곽선 두께(월드 m). 0이면 본체용
 */
export function buildMobMotion(shape: MobShape, height: number, thick = 0, skin?: SkinAtlas): MobMotionNodes {
  const H = height;
  const boar = shape === 'boar';
  const aM: N = attribute('aM', 'vec4');
  const aN: N = attribute('aN', 'vec4');
  const aT: N = attribute('aT', 'vec4');
  const mode = aM.x, prog = aM.y, ph = aM.z, hit = aM.w;
  const yaw = aN.x, sc = aN.y, spd = aN.z, flags = aN.w;
  // flags 비트: 1 엘리트, 2 방패 변종, 4 황금 고블린, 8 챔피언(render/mobs.ts가 쓴다)
  const champ = step(7.5, flags);
  const f1 = flags.sub(champ.mul(8));
  const gold = step(3.5, f1);
  const f2 = f1.sub(gold.mul(4));
  const variant = step(1.5, f2);
  const elite = f2.sub(variant.mul(2));
  const is = (s: number): N => step(abs(mode.sub(s)), 0.5);
  if (skin) {
    const smooth = attribute('aSmooth', 'vec3');
    const sk = skinNodes(skin, attribute('aPose', 'vec3'), positionGeometry,
      normalize(normalGeometry.mul(LIGHT_RAW).add(smooth.mul(1 - LIGHT_RAW))), smooth);
    // 공중제비(2026-10-14 피격 반응): 마무리타로 뜬 동안(Airborne) 한 바퀴, 처치되어 날아가는 앞부분(Dying 0~38%)에 한 바퀴 뒤로 넘어간다.
    // 몸 가운데(키 절반)를 축으로 돌리고, 한 바퀴를 다 돌아 끝나므로 클립 자세(쓰러짐)와 이어진다. 콘셉트 c-mob-sweep의 날아가는 몹.
    const spin = shape === 'shaman' ? float(0) : is(MobState.Airborne).mul(ph.div(KNOCKBACK.finisherStun).clamp(0, 1))
      .add(is(MobState.Dying).mul(smoothstep(0, 0.38, prog))).mul(-Math.PI * 2);
    const cS = cos(spin), sS = sin(spin), pivot = float(H * 0.5);
    const flip = (p: N): N => { const d = p.y.sub(pivot); return vec3(p.x, pivot.add(d.mul(cS)).sub(p.z.mul(sS)), p.z.mul(cS).add(d.mul(sS))); };
    const flipN = (n: N): N => vec3(n.x, n.y.mul(cS).sub(n.z.mul(sS)), n.z.mul(cS).add(n.y.mul(sS)));
    const cy = cos(yaw), sy = sin(yaw);
    const turn = (n: N): N => vec3(cy.mul(n.x).add(sy.mul(n.z)), n.y, cy.mul(n.z).sub(sy.mul(n.x)));
    const world = turn(flip(sk.position)).mul(sc).add(aT.xyz);
    const normalWorld = normalize(turn(flipN(sk.normal)));
    let position = world;
    if (thick > 0) {
      const inflated = world.add(normalize(turn(flipN(sk.smooth))).mul(float(thick).mul(float(1).add(elite.mul(0.8)))));
      position = inflated.add(normalize(inflated.sub(cameraPosition)).mul(sc.mul(H * 0.09)));
    }
    return { position, normalWorld, normalView: normalize(transformNormalToView(normalWorld)).toVarying('mobNV'),
      fade: is(MobState.Dying).mul(smoothstep(0.62, 1, prog)).toVarying('mobFade'), hit, elite, variant, gold, champ, phase: ph };
  }
  const mSp = is(MobState.Spawning), mId = is(MobState.Idle), mCh = is(MobState.Chase), mWi = is(MobState.Windup);
  const mAt = is(MobState.Attack), mSt = is(MobState.Stunned), mAi = is(MobState.Airborne), mDi = is(MobState.Dying);
  const eo = easeOut(prog);
  const hitOn = step(0.001, hit);

  const hop = ph.mul(boar ? 0.5 : 0.42);
  const sHop = sin(hop);
  const aHop = abs(sHop);
  const spdMix = spd.mul(0.55).add(0.45);

  // ── 굽힘(피치), 기울임(롤) ───────────────────────────
  let pitch: N;
  let roll: N;
  let sx: N, sy: N, sz: N;
  let yOff: N, zOff: N, shakeX: N;
  if (!boar) {
    pitch = mCh.mul(spd.mul(0.08).add(0.14))
      .add(mId.mul(sin(ph.mul(0.07)).mul(0.02)))
      .add(mWi.mul(eo.mul(-0.5)))
      .add(mAt.mul(float(0.75).mul(float(1).sub(prog.mul(0.6)))))
      .add(mSt.mul(-0.32))
      .add(mAi.mul(-0.3));
    roll = mCh.mul(sHop.mul(spd).mul(0.07)).add(mSt.mul(sin(ph.mul(0.7)).mul(0.12)));
    sx = float(1).add(mWi.mul(eo.mul(0.14)));
    sy = float(1)
      .add(mCh.mul(sin(hop.mul(2)).mul(0.035)))
      .add(mId.mul(sin(ph.mul(0.12)).mul(0.02)))
      .add(mWi.mul(eo.mul(0.14)))
      .add(mSp.mul(sin(prog.mul(Math.PI)).mul(0.12)));
    sz = sx;
    yOff = mCh.mul(aHop.mul(H * 0.075).mul(spdMix))
      .add(mSp.mul(float(1).sub(eo).mul(-H * 0.98)))
      .add(mDi.mul(smoothstep(0.7, 1.0, prog).mul(-H * 0.3)));
    zOff = mAt.mul(float(1).sub(prog.mul(prog)).mul(0.38)).add(mWi.mul(eo.mul(-0.08)));
    shakeX = mWi.mul(sin(ph.mul(2.2)).mul(0.03).mul(eo)).add(mSt.mul(sin(ph.mul(0.9)).mul(0.05)));
  } else {
    pitch = mCh.mul(sHop.mul(0.06).add(0.04))
      .add(mWi.mul(eo.mul(0.3)))
      .add(mAt.mul(0.1))
      .add(mSt.mul(-0.2))
      .add(mAi.mul(-0.3));
    roll = mCh.mul(sHop.mul(0.035)).add(mSt.mul(sin(ph.mul(0.7)).mul(0.1)));
    sx = float(1).add(mWi.mul(eo.mul(0.08))).sub(mAt.mul(0.05));
    sy = float(1)
      .add(mCh.mul(sin(hop.mul(2)).mul(0.03)))
      .add(mId.mul(sin(ph.mul(0.1)).mul(0.02)))
      .sub(mWi.mul(eo.mul(0.1)))
      .sub(mAt.mul(0.1))
      .add(mSp.mul(sin(prog.mul(Math.PI)).mul(0.1)));
    sz = float(1).sub(mWi.mul(eo.mul(0.06))).add(mAt.mul(0.12));
    yOff = mCh.mul(aHop.mul(H * 0.045).mul(spdMix))
      .add(mAt.mul(abs(sin(ph.mul(0.7))).mul(H * 0.05)).sub(mAt.mul(H * 0.03)))
      .sub(mWi.mul(eo.mul(H * 0.04)))
      .add(mSp.mul(float(1).sub(eo).mul(-H * 0.98)))
      .add(mDi.mul(smoothstep(0.7, 1.0, prog).mul(-H * 0.3)));
    zOff = mWi.mul(eo.mul(-0.2));
    shakeX = mWi.mul(sin(ph.mul(2.6)).mul(0.05).mul(eo)).add(mSt.mul(sin(ph.mul(0.9)).mul(0.05)));
  }
  // 피격: 눌림 + 퍼짐
  sx = sx.mul(float(1).add(hitOn.mul(0.12)));
  sy = sy.mul(float(1).sub(hitOn.mul(0.12)));
  // 등장 크기 팝, 사망 축소
  const grow = float(1).sub(mSp).add(mSp.mul(eo.mul(0.7).add(0.3))).mul(float(1).sub(mDi.mul(smoothstep(0.6, 1.0, prog).mul(0.3))));
  sx = sx.mul(grow); sy = sy.mul(grow); sz = sz.mul(grow);
  // 강체 회전(공중제비·쓰러짐)
  // 처치: 튕겨 날아가며 몸통 가운데를 축으로 빠르게 뒤로 넘어가고(ease-out), 땅에 떨어진 뒤 늦게 사라진다(DEATH_LAUNCH)
  const dieTip = float(1).sub(float(1).sub(prog).mul(float(1).sub(prog)));
  const rigid = mAi.mul(ph.mul(-0.28)).add(mDi.mul(dieTip.mul(-1.55)));
  const pivY = mAi.mul(H * 0.5).add(mDi.mul(H * 0.4));
  const fade = mDi.mul(smoothstep(0.62, 1.0, prog));

  // ── 정점 변형(모델 공간) ──────────────────────────────
  const p0: N = positionGeometry;
  const n0: N = normalGeometry;
  const hN = p0.y.div(H);
  const w = boar ? smoothstep(0.05, 0.7, hN) : smoothstep(0.15, 0.85, hN);
  const y0 = H * (boar ? 0.15 : 0.35);

  const qx = p0.x.mul(sx), qy = p0.y.mul(sy), qz = p0.z.mul(sz);
  const th = pitch.mul(w);
  const cT = cos(th), sT = sin(th);
  const dy = qy.sub(y0);
  const y1 = float(y0).add(dy.mul(cT)).sub(qz.mul(sT));
  const z1 = qz.mul(cT).add(dy.mul(sT));
  const tr = roll.mul(w);
  const cR = cos(tr), sR = sin(tr);
  const dy1 = y1.sub(y0);
  const x2 = qx.mul(cR).sub(dy1.mul(sR));
  const y2 = float(y0).add(qx.mul(sR)).add(dy1.mul(cR));
  const cG = cos(rigid), sG = sin(rigid);
  const dy2 = y2.sub(pivY);
  const y3 = pivY.add(dy2.mul(cG)).sub(z1.mul(sG));
  const z3 = z1.mul(cG).add(dy2.mul(sG));
  const lx = x2.add(shakeX.mul(w));
  const ly = y3.add(yOff);
  const lz = z3.add(zOff);

  const cy = cos(yaw), sy2 = sin(yaw);
  /** 방향 벡터에 같은 회전(피치 → 롤 → 강체 → yaw)을 적용한다(크기 변형은 무시). */
  const rotate = (n: N): N => {
    const ny1 = n.y.mul(cT).sub(n.z.mul(sT));
    const nz1 = n.z.mul(cT).add(n.y.mul(sT));
    const nx2 = n.x.mul(cR).sub(ny1.mul(sR));
    const ny2 = n.x.mul(sR).add(ny1.mul(cR));
    const ny3 = ny2.mul(cG).sub(nz1.mul(sG));
    const nz3 = nz1.mul(cG).add(ny2.mul(sG));
    return normalize(vec3(cy.mul(nx2).add(sy2.mul(nz3)), ny3, cy.mul(nz3).sub(sy2.mul(nx2))));
  };

  const wx = cy.mul(lx).add(sy2.mul(lz));
  const wz = cy.mul(lz).sub(sy2.mul(lx));
  const worldPos: N = vec3(wx.mul(sc).add(aT.x), ly.mul(sc).add(aT.y), wz.mul(sc).add(aT.z));
  // 조명 법선: 원래 정점 법선과 매끈 법선을 섞어 구겨진 면의 툰 조각을 줄인다(텍스처가 세부를 맡는다)
  const nSmooth: N = attribute('aSmooth', 'vec3');
  const normalWorld: N = rotate(normalize(n0.mul(LIGHT_RAW).add(nSmooth.mul(1 - LIGHT_RAW))));

  let position: N = worldPos;
  if (thick > 0) {
    // 외곽선: 위치가 같은 정점끼리 평균 낸 매끈 법선(aSmooth)으로 부풀리고, 시선 방향으로 뒤로 밀어
    // 구겨진 면의 오목한 곳에서 헐이 몸을 뚫고 나오지 않게 한다(화면 위치는 그대로, 깊이만 뒤로).
    const ns: N = rotate(nSmooth);
    const inflated = worldPos.add(ns.mul(float(thick).mul(float(1).add(elite.mul(0.8)))));
    position = inflated.add(normalize(inflated.sub(cameraPosition)).mul(sc.mul(H * 0.09)));
  }
  return {
    position,
    normalWorld,
    normalView: normalize(transformNormalToView(normalWorld)).toVarying('mobNV'),
    fade: fade.toVarying('mobFade'),
    hit,
    elite,
    variant,
    gold,
    champ,
    phase: ph,
  };
}
